import React, { useEffect, useRef, useState } from "react";
import { Operations, OperationOptions } from "../../MathFactsGenerator/Constants.ts";
import { AnswerResult } from "../Quiz/answer.ts";
import { cardFaces } from "../cardFaces.ts";
import { PickIndex } from "./chartGame.ts";
import { signFor } from "./FactPicker.tsx";
import {
    COLS,
    Direction,
    directionTo,
    LIVES,
    Mistake,
    moveMuncher,
    munch,
    MULTIPLICATION_ONLY,
    MunchState,
    remaining,
    ROWS,
    Rule,
    Square,
    startMunch,
    stepFrom,
    tick,
} from "./munchGame.ts";
import { useTranslation } from "../../Shared/i18n/useTranslation.ts";
import { format } from "../../Shared/i18n/format.ts";
import { StringKey } from "../../Shared/i18n/strings.ts";

type Translate = (key: StringKey) => string;

export interface MunchResult {
    calm: boolean;
    munched: number;
    wrong: number;
    level: number;
    mistakes: Mistake[];
}

interface MunchGameProps {
    calm: boolean;
    pick: PickIndex;
    /** What the player chose to practise; "Makes n" squares are facts of these (ADR 0098 §4). */
    operations?: string[];
    /** A "Makes n" square munched - the only kind that is a fact (ADR 0056 §5). `front` is the generator's own text, "7 x 8". */
    onFactMunched(front: string, result: AnswerResult): void;
    onFinish(result: MunchResult): void;
}

const KEYS: Record<string, Direction> = {
    ArrowUp: "up",
    ArrowDown: "down",
    ArrowLeft: "left",
    ArrowRight: "right",
    w: "up",
    s: "down",
    a: "left",
    d: "right",
};

/** The d-pad, in grid order. `labelKey` is a translation key, not display text. */
const PAD: { action: Direction | "munch"; labelKey: StringKey; glyph: string | null }[] = [
    { action: "up", labelKey: "flashCards.munchUp", glyph: "↑" },
    { action: "left", labelKey: "flashCards.munchLeft", glyph: "←" },
    { action: "munch", labelKey: "flashCards.munchMunchButton", glyph: null },
    { action: "right", labelKey: "flashCards.munchRight", glyph: "→" },
    { action: "down", labelKey: "flashCards.munchDown", glyph: "↓" },
];

const ROW_INDEXES = Array.from({ length: ROWS }, (_, r) => r);
const COL_INDEXES = Array.from({ length: COLS }, (_, c) => c);

/** Anything shown on a square or tile: a number, or an `a op b` fact. */
export type Shown = Pick<Square, "value" | "op" | "a" | "b">;

const operationOf = (square: Shown): string => square.op ?? Operations.Multiplication;

/** The fact as the board prints it, with the signs a child writes: 5 + 7, 15 − 3, 3 × 4, 24 ÷ 2. */
export function squareText(square: Shown): string {
    return square.a !== undefined && square.b !== undefined
        ? `${square.a} ${signFor(operationOf(square))} ${square.b}`
        : String(square.value);
}

/** The same fact as the quiz and the generator write it ("7 x 8"), the form the missed-facts list keys on. */
export function factFront(square: Shown): string {
    const sign = OperationOptions.find((op) => op.value === operationOf(square))?.sign ?? "x";
    return cardFaces({ key: 0, numbers: [square.a ?? 0, square.b ?? 0, square.value], operator: sign }).front;
}

export function ruleText(rule: Rule, t: Translate): string {
    const template =
        rule.kind === "multiples"
            ? t("flashCards.munchRuleMultiples")
            : rule.kind === "makes"
            ? t("flashCards.munchRuleProducts")
            : t("flashCards.munchRuleFactors");
    return format(template, { n: rule.n });
}

/** Why a munched square did not fit - the teaching half of a lost life (§1). */
export function explain(rule: Rule, square: Shown, t: Translate): string {
    const { n } = rule;
    const v = square.value;
    if (rule.kind === "multiples") {
        const k = Math.floor(v / n);
        return k === 0
            ? format(t("flashCards.munchWhyMultipleSmallTemplate"), { v, n })
            : format(t("flashCards.munchWhyMultipleTemplate"), { v, n, k, lo: n * k, k1: k + 1, hi: n * (k + 1) });
    }
    if (rule.kind === "factors") {
        return format(t("flashCards.munchWhyFactorTemplate"), { v, n, q: Math.floor(n / v), r: n % v });
    }
    const template =
        operationOf(square) === Operations.Addition
            ? t("flashCards.munchWhyAddTemplate")
            : operationOf(square) === Operations.Subtraction
            ? t("flashCards.munchWhySubtractTemplate")
            : operationOf(square) === Operations.Division
            ? t("flashCards.munchWhyDivideTemplate")
            : t("flashCards.munchWhyProductTemplate");
    return format(template, { front: squareText(square), p: v, n });
}

/**
 * One game of Math Munch (ADR 0056). The engine is pure (`munchGame.ts`);
 * this feeds it key presses, taps and animation-frame deltas, holding state in
 * a ref with a snapshot per frame, as DropGameRun does.
 *
 * The board keeps keyboard focus: the d-pad buttons refuse it on mouse-down,
 * so the arrow keys keep working after a tap.
 */
export function MunchGame({ calm, pick, operations = MULTIPLICATION_ONLY, onFactMunched, onFinish }: MunchGameProps) {
    const t = useTranslation();
    const stateRef = useRef<MunchState | null>(null);
    if (stateRef.current === null) {
        stateRef.current = startMunch(calm, pick, operations);
    }
    const [view, setView] = useState<MunchState>(stateRef.current);
    const [paused, setPaused] = useState(false);
    const [message, setMessage] = useState<{ text: string; tone: "good" | "bad" } | null>(null);
    const boardRef = useRef<HTMLDivElement>(null);
    const handledSeq = useRef(0);
    const reported = useRef(false);

    const over = view.phase === "over";

    function apply(change: (s: MunchState) => MunchState) {
        if (paused) {
            return;
        }
        stateRef.current = change(stateRef.current!);
        setView(stateRef.current);
    }

    useEffect(() => {
        if (paused || over) {
            return;
        }
        let frame = 0;
        let last = performance.now();
        const loop = (now: number) => {
            stateRef.current = tick(stateRef.current!, now - last, pick);
            last = now;
            setView(stateRef.current);
            if (stateRef.current.phase !== "over") {
                frame = requestAnimationFrame(loop);
            }
        };
        frame = requestAnimationFrame(loop);
        return () => cancelAnimationFrame(frame);
    }, [paused, over, pick]);

    useEffect(() => {
        const onVisibility = () => {
            if (document.hidden) {
                setPaused(true);
            }
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => document.removeEventListener("visibilitychange", onVisibility);
    }, []);

    useEffect(() => {
        if (!paused) {
            boardRef.current?.focus();
        }
    }, [paused]);

    // Each event is handled once, keyed on its sequence number.
    const event = view.lastEvent;
    useEffect(() => {
        if (!event || event.seq === handledSeq.current) {
            return;
        }
        handledSeq.current = event.seq;
        const s = stateRef.current!;
        if (event.kind === "munched") {
            setMessage({ text: format(t("flashCards.munchYumTemplate"), { text: squareText(event.square) }), tone: "good" });
            if (event.square.a !== undefined) {
                onFactMunched(factFront(event.square), "correct");
            }
        } else if (event.kind === "wrong") {
            const rule = s.mistakes[s.mistakes.length - 1].rule;
            setMessage({ text: `${explain(rule, event.square, t)} ${t("flashCards.munchLostLife")}`, tone: "bad" });
            if (event.square.a !== undefined) {
                onFactMunched(factFront(event.square), "wrong");
            }
        } else if (event.kind === "caught") {
            setMessage({ text: t("flashCards.munchCaught"), tone: "bad" });
        } else {
            setMessage(null);
        }
    }, [event, onFactMunched, t]);

    useEffect(() => {
        if (over && !reported.current) {
            reported.current = true;
            onFinish({ calm, munched: view.munched, wrong: view.wrong, level: view.level, mistakes: view.mistakes });
        }
    }, [over, view, calm, onFinish]);

    function onKeyDown(e: React.KeyboardEvent) {
        const dir = KEYS[e.key] ?? KEYS[e.key.toLowerCase()];
        if (dir) {
            e.preventDefault();
            apply((s) => moveMuncher(s, dir));
        } else if (e.key === " " || e.key === "Enter") {
            e.preventDefault();
            apply(munch);
        }
    }

    function tapSquare(index: number) {
        boardRef.current?.focus();
        const s = stateRef.current!;
        if (index === s.player) {
            apply(munch);
            return;
        }
        const dir = directionTo(s.player, index);
        if (dir) {
            apply((st) => moveMuncher(st, dir));
        }
    }

    const keepBoardFocus = (e: React.MouseEvent) => e.preventDefault();
    const monsterAt = new Set(view.monsters.map((m) => m.pos));
    const nextTo = new Set(
        (["up", "down", "left", "right"] as Direction[]).map((d) => stepFrom(view.player, d))
    );

    function squareLabel(index: number): string {
        const square = view.squares[index];
        const parts = [square.eaten ? t("flashCards.munchSquareEaten") : squareText(square)];
        if (index === view.player) parts.push(t("flashCards.munchSquareYou"));
        if (monsterAt.has(index)) parts.push(t("flashCards.munchSquareMonster"));
        return parts.join(", ");
    }

    return (
        <div className="munchGame">
            <div className="quizStatus munchStatus">
                <span className="quizScore">{format(t("flashCards.scoreTemplate"), { n: view.munched })}</span>
                <span className="dropLevel">{format(t("flashCards.gameLevelTemplate"), { n: view.level })}</span>
                <span
                    className="dropLives"
                    role="img"
                    aria-label={
                        view.lives === 1
                            ? t("flashCards.gameOneLifeAria")
                            : format(t("flashCards.gameLivesAriaTemplate"), { n: view.lives })
                    }
                >
                    {"♥".repeat(view.lives)}
                    <span className="dropLivesLost">{"♥".repeat(LIVES - view.lives)}</span>
                </span>
            </div>

            <p className="munchRule" aria-live="polite">
                {view.phase === "levelDone" ? t("flashCards.munchLevelDone") : ruleText(view.rule, t)}
            </p>
            <p className="munchLeft">
                {view.phase === "playing" && format(t("flashCards.munchLeftTemplate"), { n: remaining(view) })}
            </p>

            <div
                ref={boardRef}
                className={`munchBoard${paused ? " munchBoard--paused" : ""}`}
                tabIndex={0}
                role="application"
                aria-label={t("flashCards.munchBoardLabel")}
                onKeyDown={onKeyDown}
            >
                <table className="munchGrid">
                    <tbody>
                        {ROW_INDEXES.map((r) => (
                            <tr key={r}>
                                {COL_INDEXES.map((c) => {
                                    const index = r * COLS + c;
                                    const square = view.squares[index];
                                    const classes = [
                                        "munchSquare",
                                        square.eaten ? "munchSquare--eaten" : "",
                                        index === view.player ? "munchSquare--you" : "",
                                        monsterAt.has(index) ? "munchSquare--monster" : "",
                                        nextTo.has(index) ? "munchSquare--near" : "",
                                    ]
                                        .filter(Boolean)
                                        .join(" ");
                                    return (
                                        <td
                                            key={c}
                                            className={classes}
                                            aria-label={squareLabel(index)}
                                            onClick={() => tapSquare(index)}
                                        >
                                            {!square.eaten && <span className="munchValue">{squareText(square)}</span>}
                                            {index === view.player && <span className="muncher" aria-hidden="true" />}
                                            {monsterAt.has(index) && <span className="munchMonster" aria-hidden="true" />}
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
                {paused && <p className="munchPausedBanner">{t("flashCards.gamePaused")}</p>}
            </div>

            <p className={`munchMessage${message ? ` munchMessage--${message.tone}` : ""}`} aria-live="polite">
                {message?.text}
            </p>

            <div className="munchPad">
                {PAD.map(({ action, labelKey: label, glyph }) => (
                    <button
                        key={action}
                        type="button"
                        className={`munchPadButton munchPad--${action}`}
                        aria-label={t(label)}
                        disabled={paused || over}
                        onMouseDown={keepBoardFocus}
                        onClick={() => apply(action === "munch" ? munch : (s) => moveMuncher(s, action))}
                    >
                        {glyph ?? t(label)}
                    </button>
                ))}
            </div>

            <button
                type="button"
                className="quizFinish dropPause"
                onClick={() => setPaused((p) => !p)}
                disabled={over}
            >
                {paused ? t("flashCards.gameResume") : t("flashCards.gamePause")}
            </button>
        </div>
    );
}
